import { Field, Input, Tag, TagGroup, makeStyles, tokens } from '@fluentui/react-components';
import type { RecipientWarning } from '@gandhi/mail-protocol';
import type { ReactElement } from 'react';
import { joinRecipients, parseAddressList, splitRecipients } from '../state/compose.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 4px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    background: tokens.colorNeutralBackground1,
  },
  input: { flex: 1, minWidth: '160px' },
  flagged: {
    border: `1px solid ${tokens.colorStatusWarningBorder2}`,
    color: tokens.colorStatusWarningForeground1,
  },
});

export interface RecipientFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onCommit: () => void;
  readonly warnings: readonly RecipientWarning[];
  readonly required?: boolean;
}

/**
 * Addresses become chips as soon as they are finished, so a recipient is a thing you
 * can see and remove rather than a fragment of a comma-separated string. A recipient
 * the warnings name is marked here, next to the address, not only in the banner above.
 */
export function RecipientField(props: RecipientFieldProps): ReactElement {
  const styles = useStyles();
  const { label, value, onChange, onCommit, warnings, required = false } = props;
  const { committed, pending } = splitRecipients(value);

  const commit = (): void => {
    if (pending.trim() === '') return;
    onChange(joinRecipients([...committed, ...parseAddressList(pending)], ''));
  };

  const warningFor = (address: string): RecipientWarning | undefined =>
    warnings.find((w) => w.address.toLowerCase() === address.toLowerCase());

  return (
    <Field label={label} required={required}>
      <div className={styles.root}>
        {committed.length > 0 && (
          <TagGroup
            aria-label={`${label} recipients`}
            onDismiss={(_event, data) =>
              onChange(
                joinRecipients(
                  committed.filter((_recipient, index) => String(index) !== data.value),
                  pending,
                ),
              )
            }
          >
            {committed.map((recipient, index) => {
              const warning = warningFor(recipient.address);
              return (
                <Tag
                  key={`${recipient.address}-${index}`}
                  value={String(index)}
                  dismissible
                  aria-label={`Remove ${recipient.address}`}
                  className={warning === undefined ? undefined : styles.flagged}
                  data-warned={warning === undefined ? undefined : warning.kind}
                  title={warning?.message}
                >
                  {recipient.name ?? recipient.address}
                </Tag>
              );
            })}
          </TagGroup>
        )}
        <Input
          className={styles.input}
          appearance="underline"
          value={pending}
          onChange={(_event, data) => onChange(joinRecipients(committed, data.value))}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            commit();
          }}
          onBlur={() => {
            commit();
            onCommit();
          }}
        />
      </div>
    </Field>
  );
}
